import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import {
  FolderOpen,
  Engineering as MoldIcon,
  Assignment as TaskIcon,
  TrendingUp as TrendIcon,
  PlayCircleOutline as InProgressIcon,
  EditNote as PlanningIcon,
  CheckCircle as CompletedIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getDashboardStats,
  getRecentProjects,
  DashboardStats,
} from '../services/dashboard.service';
import { Project } from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalProjectCount: 0,
    inProgressProjectCount: 0,
    planningProjectCount: 0,
    completedProjectCount: 0,
    moldCount: 0,
    monthlyGrowth: 0,
  });
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);

  // 데이터 로드
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statsData, projectsData] = await Promise.all([
        getDashboardStats(),
        getRecentProjects(5),
      ]);
      
      setStats(statsData);
      setRecentProjects(projectsData);
    } catch (err: any) {
      setError(err.message || '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 날짜 포맷
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ko-KR');
  };

  // 상태 설정
  const getStatusConfig = (status: string) => {
    const configs = {
      '계획': {
        color: '#FFFFFF',
        bgColor: '#3b82f6',
      },
      '진행중': {
        color: '#FFFFFF',
        bgColor: '#10b981',
      },
      '완료': {
        color: '#FFFFFF',
        bgColor: '#6b7280',
      },
      '보류': {
        color: '#FFFFFF',
        bgColor: '#f59e0b',
      }
    };
    return configs[status as keyof typeof configs] || configs['계획'];
  };

  const statsConfig = [
    {
      title: '전체 프로젝트',
      value: stats.totalProjectCount,
      icon: <FolderOpen sx={{ fontSize: 40 }} />,
      color: '#6366f1',
      path: '/projects?status=all',
    },
    {
      title: '진행중 프로젝트',
      value: stats.inProgressProjectCount,
      icon: <InProgressIcon sx={{ fontSize: 40 }} />,
      color: '#10b981',
      path: '/projects?status=진행중',
    },
    {
      title: '계획 프로젝트',
      value: stats.planningProjectCount,
      icon: <PlanningIcon sx={{ fontSize: 40 }} />,
      color: '#3b82f6',
      path: '/projects?status=계획',
    },
    {
      title: '완료된 프로젝트',
      value: stats.completedProjectCount,
      icon: <CompletedIcon sx={{ fontSize: 40 }} />,
      color: '#6b7280',
      path: '/projects?status=완료',
    },
    {
      title: '등록된 금형',
      value: stats.moldCount,
      icon: <MoldIcon sx={{ fontSize: 40 }} />,
      color: '#f59e0b',
      path: '/molds',
    },
    {
      title: '이번달 증가율',
      value: `${stats.monthlyGrowth > 0 ? '+' : ''}${stats.monthlyGrowth}%`,
      icon: <TrendIcon sx={{ fontSize: 40 }} />,
      color: stats.monthlyGrowth >= 0 ? '#10b981' : '#ef4444',
      path: '/reports',
    },
  ];
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* 헤더 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          대시보드
        </Typography>
        <Typography variant="body1" color="text.secondary">
          안녕하세요, {user?.displayName || user?.email}님! 👋
        </Typography>
      </Box>

      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 로딩 */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* 통계 카드 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            {statsConfig.map((stat, index) => (
          <Box 
            key={index}
            sx={{ 
              flex: '0 0 auto',
              width: { 
                xs: '100%',                      // 모바일: 1칸
                sm: 'calc(33.333% - 16px)',     // 태블릿: 3칸
                lg: 'calc(16.666% - 20px)'      // PC: 6칸
              }
            }}
          >
            <Card
              onClick={() => navigate(stat.path)}
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}05 100%)`,
                border: `1px solid ${stat.color}30`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 16px ${stat.color}40`,
                  borderColor: stat.color,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      color: stat.color,
                      mr: 2,
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
      
      {/* 최근 프로젝트 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          최근 프로젝트
        </Typography>
        
        {recentProjects.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                등록된 프로젝트가 없습니다.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                프로젝트 메뉴에서 새 프로젝트를 등록하세요.
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {recentProjects.map((project) => (
              <Box
                key={project.id}
                sx={{ 
                  flex: '0 0 auto',
                  width: { 
                    xs: '100%',
                    sm: 'calc(50% - 12px)',
                    md: 'calc(25% - 18px)'
                  }
                }}
              >
                <Card
                  elevation={1}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <CardContent sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="bold" 
                      gutterBottom
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {project.projectName}
                    </Typography>
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {project.projectNo}
                    </Typography>
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 2,
                      }}
                    >
                      {project.customerName || '-'}
                    </Typography>
                    
                    <Box sx={{ mt: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        label={project.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusConfig(project.status).bgColor,
                          color: getStatusConfig(project.status).color,
                          fontWeight: 500,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        금형 {project.moldCount || 0}개
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>
        </>
      )}
    </Container>
  );
};

export default Dashboard;